import React, { useState, useEffect, useCallback, useRef } from 'react';
import { flushSync } from 'react-dom';
import Icon from '../shared/Icon';
import { Responsive, WidthProvider } from 'react-grid-layout';
import { useNavigate } from 'react-router-dom';
import { useFlag } from '@unleash/proxy-client-react';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import './Insights.css';
import { apiService } from '../../../services/api';
import WidgetCreatorModal from './WidgetCreatorModal';
import WidgetTextEditModal, { WIDGET_TEXT_EDIT_FLAG } from './WidgetTextEditModal';
import { withoutServerOnlyFields, withTextEdit, savedRefusal } from './widgetPayload';
import WidgetCard from './WidgetCard';
import { createFailureBatcher, QUIESCENCE_BACKSTOP_MS } from './notifyBatcher';
import { onWidgetQueriesQuiescent, resetWidgetQueryState } from './useWidgetQuery';
import { useConfirm } from '../shared/useConfirm';
import { COLS, buildLayouts } from './layoutUtils';
import DashboardTabBar from './DashboardTabBar';
import { formatTimestamp } from '../../../shared/datetime/timestamps';
import { useDashboardTabs } from './useDashboardTabs';
import {
  TAB_STATE,
  importFromBrowserLayout,
  layoutPayload,
  ownTabs,
  sameLayout,
  effectiveLayouts,
  savedLayoutsFromWidgets,
  visibleTabs,
  widgetsForTab,
} from './tabsUtils';

const ResponsiveGridLayout = WidthProvider(Responsive);
const LAYOUT_STORAGE_KEY = 'insights_grid_layouts';
const BREAKPOINTS = { lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 };
const ROW_HEIGHT = 70;

function getGreeting() {
  const hour = new Date().getHours();
  if (hour >= 5 && hour < 12) return 'Good morning';
  if (hour >= 12 && hour < 17) return 'Good afternoon';
  return 'Good evening';
}

function capitalize(str) {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

function getDisplayName(user) {
  // PRD-09: prefer the user's editable display_name; fall back to the SSO-sourced name.
  const name = user?.profile?.display_name || user?.name;
  if (!name || typeof name !== 'string') return '';
  const trimmed = name.trim();

  if (trimmed.includes('@')) {
    const localPart = trimmed.split('@')[0];
    const parts = localPart.split(/[._-]/).filter(Boolean);
    if (parts.length === 0) return '';
    return capitalize(parts[0]);
  }

  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  return capitalize(parts[0]);
}

function loadSavedLayouts() {
  try {
    return JSON.parse(localStorage.getItem(LAYOUT_STORAGE_KEY) || '{}');
  } catch {
    return {};
  }
}

function saveLayouts(layouts) {
  try {
    localStorage.setItem(LAYOUT_STORAGE_KEY, JSON.stringify(layouts));
  } catch {
    /* localStorage may be full / disabled — silently ignore */
  }
}

// Dashboard tabs keep layout on the server. The first time a user opens Insights with
// tabs on, whatever this browser had saved is moved across once, so nobody's
// arrangement resets on the switch. The marker is per user because a browser can be
// shared; it is only set once the server has the layout, so a failed import is retried
// on the next load rather than lost.
const LAYOUT_IMPORTED_KEY = 'insights_grid_layouts_imported';

// Returns the widgets with their imported positions, and the user's own positions
// for colleagues' widgets per tab, to apply on screen. Both are applied whatever
// happens to the save, so the old arrangement shows either way.
async function importBrowserLayout(widgets, userId, tabs) {
  const marker = `${LAYOUT_IMPORTED_KEY}:${userId}`;
  const nothing = { widgets, overrides: {} };
  try {
    if (localStorage.getItem(marker) === '1') return nothing;
  } catch {
    return nothing;
  }
  const byTab = importFromBrowserLayout(loadSavedLayouts(), widgets, tabs);
  const mine = {};
  const overrides = {};
  const isShared = new Map(widgets.map((w) => [w.id, w.is_shared]));
  for (const [tabId, payload] of Object.entries(byTab)) {
    for (const [id, cells] of Object.entries(payload)) {
      if (isShared.get(id)) {
        const existing = (tabs || []).find((t) => t.id === tabId)?.layout_overrides || {};
        overrides[tabId] = { ...existing, ...(overrides[tabId] || {}), [id]: cells };
      } else {
        mine[id] = cells;
      }
    }
  }
  const merged = widgets.map((w) => (mine[w.id] ? { ...w, layout: mine[w.id] } : w));
  try {
    await Promise.all(
      // A tab's request carries the user's existing positions for colleagues'
      // widgets too: the server replaces that set with what it is sent, and an import
      // run again from a second browser must not wipe what the first one stored.
      Object.entries(byTab).map(([tabId, payload]) => {
        const kept = (tabs || []).find((t) => t.id === tabId)?.layout_overrides || {};
        return apiService.saveDashboardTabLayout(tabId, { ...kept, ...payload });
      }),
    );
    localStorage.setItem(marker, '1');
  } catch (err) {
    console.error('[Insights] Could not move the saved layout to the server; will retry:', err);
  }
  return { widgets: merged, overrides };
}

function Insights({ user, onShowNotification }) {
  const [greeting, setGreeting] = useState(getGreeting());
  // The time on the print header, which is the only time on the paper: the moment of
  // printing, not of the last render, so a dashboard left open and printed later says
  // when it was printed. Rendered synchronously, before the browser lays out the page.
  const [printedAt, setPrintedAt] = useState(() => new Date());
  useEffect(() => {
    const stamp = () => flushSync(() => setPrintedAt(new Date()));
    window.addEventListener('beforeprint', stamp);
    return () => window.removeEventListener('beforeprint', stamp);
  }, []);
  const [showCreator, setShowCreator] = useState(false);
  const [editingWidget, setEditingWidget] = useState(null);
  // PRD-1578: the widget whose words are being edited (title, or a KPI's label).
  const [textEditingWidget, setTextEditingWidget] = useState(null);
  const [widgets, setWidgets] = useState([]);
  const [savedLayouts, setSavedLayouts] = useState(loadSavedLayouts);
  const [exporting, setExporting] = useState(false);
  // PRD-1370 #5: the dashboard-level actions menu (Export PDF / Share dashboard).
  const [dashMenuOpen, setDashMenuOpen] = useState(false);
  const dashMenuRef = useRef(null);
  const [confirm, confirmDialog] = useConfirm();
  const displayName = getDisplayName(user);
  const navigate = useNavigate();

  // PRD-1337: chart-to-chat drill-down.
  const drilldownEnabled =
    useFlag('enable_insights_drilldown') || process.env.REACT_APP_FORCE_INSIGHTS_DRILLDOWN === 'true';

  // PRD-1336 R6: export the dashboard as a PDF.
  const exportEnabled = useFlag('enable_insights_export');
  // PRD-1578: text-only Edit and the KPI label. Handlers are gated too, not just the menu.
  const textEditEnabled = useFlag(WIDGET_TEXT_EDIT_FLAG);
  // PRD-1336 R7: org widget sharing. Gates the "Share dashboard" action and the
  // per-card Share, so a tenant without sharing never sees an action it can't use
  // (review on #188). The creator modal already gates on this same flag.
  const sharingEnabled = useFlag('org-widget-sharing');
  // Dashboard tabs. OFF is the single-page dashboard exactly as before, layout in
  // this browser included.
  const tabsFlag = useFlag('insights-dashboard-tabs');
  // Set when the flag says tabs but they could not be loaded -- most likely the
  // server's flag disagrees with this browser's, or the call failed. The page then
  // runs as the single-page dashboard for this visit instead of showing nothing.
  const [tabsUnavailable, setTabsUnavailable] = useState(false);
  const tabsEnabled = tabsFlag && !tabsUnavailable;
  const tabsApi = useDashboardTabs({
    enabled: tabsFlag,
    userId: user?.id,
    notify: onShowNotification,
    confirm,
  });
  const loadTabs = tabsApi.load;
  const { setLayoutOverrides } = tabsApi;

  useEffect(() => { setGreeting(getGreeting()); }, []);

  // Widget failures go through a batcher: the Console shows one toast at a time,
  // so a dashboard-wide outage must arrive as one message, not N overwrites.
  // Held in refs, not a memo keyed on the callback: App's showNotification is a new
  // function every render (and firing a toast re-renders App), so a memo would
  // rebuild the batcher — and lose its window — the moment it first fired. The
  // batcher reads the latest callback through a ref and keeps one identity for the
  // life of the page; unmount cancels anything still pending so a toast scheduled
  // here never lands on another view.
  const onShowNotificationRef = useRef(onShowNotification);
  onShowNotificationRef.current = onShowNotification;
  const batcherRef = useRef(null);
  if (!batcherRef.current) {
    batcherRef.current = createFailureBatcher(
      (message, type) => onShowNotificationRef.current?.(message, type),
      // The timer is only a backstop now; the flush below is what normally fires.
      QUIESCENCE_BACKSTOP_MS,
    );
  }
  // A card reports (name, reason, full). Memoised because this is a prop on every
  // WidgetCard, and a fresh identity each render would re-run their notification
  // effects.
  const notifyWidgetFailure = useCallback(
    (name, reason, full) => batcherRef.current?.report(name, reason, full),
    [],
  );
  // Flush when the last widget query settles rather than on a clock. A dashboard load
  // runs three queries at a time and each is an agent run, so it arrives in waves and
  // a fixed window produced one toast per wave, each overwriting the last.
  //
  // Subscribed HERE rather than inside the batcher, and torn down with the effect:
  // the ref survives React's double-mount but an effect cleanup does not, so a
  // subscription owned by the batcher was cancelled on the first unmount and never
  // restored.
  useEffect(() => {
    const unsubscribe = onWidgetQueriesQuiescent(() => batcherRef.current?.flush());
    return () => {
      unsubscribe();
      batcherRef.current?.cancel();
      // A query still queued when the page is left never settles, so its begin/end
      // pair never closes and the count stays above zero for the session. Clear the
      // accounting on the way out rather than leaving quiescence permanently dead.
      resetWidgetQueryState();
    };
  }, []);

  // Load widgets from API on mount. With tabs on, the tabs load first: that call is
  // also what places widgets saved before tabs existed onto the first tab, so the
  // widget list read after it already carries their tab.
  const reloadWidgets = useCallback(async () => {
    const list = await apiService.listWidgets();
    setWidgets(list);
    return list;
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Tabs failing must not take the widgets down with them: the widgets are fine,
      // only the tab call is not. Kept out of the widget load's try for that reason.
      let tabsLoaded = false;
      let loadedTabs = [];
      if (tabsFlag) {
        try {
          loadedTabs = (await loadTabs()) || [];
          tabsLoaded = true;
        } catch (err) {
          console.error('[Insights] Tabs could not be loaded; showing one page:', err);
          if (!cancelled) {
            setTabsUnavailable(true);
            onShowNotification?.('Tabs are unavailable right now, so your widgets are shown on one page.', 'info');
          }
        }
      }
      try {
        const list = await apiService.listWidgets();
        if (!tabsLoaded) {
          if (!cancelled) setWidgets(list);
          return;
        }
        const { widgets: placed, overrides } = await importBrowserLayout(list, user?.id, loadedTabs);
        if (cancelled) return;
        setWidgets(placed);
        Object.entries(overrides).forEach(([tabId, o]) => setLayoutOverrides(tabId, o));
      } catch (err) {
        console.error('[Insights] Failed to load widgets:', err);
        onShowNotification?.('Failed to load widgets', 'error');
      }
    })();
    return () => { cancelled = true; };
  }, [tabsFlag, loadTabs, setLayoutOverrides, user?.id, onShowNotification]);

  // What the grid shows. With tabs on, the active tab's widgets; a shared-with-you tab
  // is read-only and drawn in its owner's layout.
  const activeTab = tabsEnabled ? tabsApi.activeTab : null;
  const viewingShared = activeTab?.state === TAB_STATE.SHARED_WITH_YOU;
  const shownWidgets = tabsEnabled ? widgetsForTab(widgets, tabsApi.tabs, activeTab?.id) : widgets;
  // Each widget's position as this user sees it: their own widgets' saved layout,
  // and their own sizes for anyone else's (see effectiveLayouts).
  const tabLayouts = effectiveLayouts(shownWidgets, activeTab);
  // The widgets whose layout this user may store from this tab: their own when the
  // tab is theirs, and every other widget drawn here, into their own copy.
  const savableIds = shownWidgets
    .filter((w) => (w.is_shared ? true : activeTab?.is_own && w.tab_id === activeTab.id))
    .map((w) => w.id);

  const handleSave = useCallback((widget) => {
    const payload = {
      title: widget.title,
      widget_type_id: widget.widget_type_id,
      prompt: widget.prompt,
      dataset_ids: widget.dataset_ids,
      data: withoutServerOnlyFields(widget.data),
      x_label: widget.x_label,
      y_label: widget.y_label,
      // PRD-1336 R1: width / widget_size are no longer written — the canvas owns
      // the size. Existing DB columns are left as-is (leave-and-ignore).
      time_range: widget.timeRange,
      // PRD-1336 R3: refresh policy.
      refresh_policy: widget.refreshPolicy,
      refresh_time: widget.refreshTime,
      refresh_interval_minutes: widget.refreshInterval,
      // PRD-1336 R7: org-wide sharing.
      visibility: widget.visibility,
      // A composite widget's tile CONFIGURATION. Its own column rather than part of
      // `data`, because the refresh write-back replaces `data` wholesale and would
      // otherwise erase the widget's definition on the first refresh.
      tiles: widget.tiles ?? null,
      // PRD-1578: a KPI's label, from the wizard's last step. Undefined for every
      // other type, which the server reads as "leave it alone".
      display_label: textEditEnabled ? widget.displayLabel : undefined,
      // A new widget goes on the tab it was made on. Never a shared-with-you tab: Add
      // widget is not offered there, and the server refuses it anyway.
      tab_id: tabsEnabled && activeTab?.is_own ? activeTab.id : undefined,
    };
    if (widget.isEdit && widget.id) {
      apiService.updateWidget(widget.id, payload)
        .then((updated) => setWidgets((prev) => prev.map((w) => (w.id === updated.id ? updated : w))))
        .catch((err) => {
          console.error('[Insights] Failed to update widget:', err);
          onShowNotification?.(savedRefusal(err) || 'Failed to update widget', 'error');
        });
    } else {
      apiService.saveWidget(payload)
        .then((saved) => setWidgets((prev) => [...prev, saved]))
        .catch((err) => {
          console.error('[Insights] Failed to save widget:', err);
          onShowNotification?.(savedRefusal(err) || 'Failed to save widget', 'error');
        });
    }
  }, [onShowNotification, tabsEnabled, activeTab, textEditEnabled]);

  // PRD-1336 R4: a widget refreshed (background-on-stale or manual). Update the
  // in-session cache immediately, then persist best-effort so the fresh result +
  // compute time survive across page loads. Persistence is owner-only server-side
  // (a viewer without write access gets a 404) — swallowed so a viewer's refresh
  // still updates their own view without an error toast.
  const handleWidgetRefreshed = useCallback((id, data, metadata) => {
    const nowIso = new Date().toISOString();
    // R6 provenance: nest the resolved absolute range into the stored result so it
    // survives a page load and prints on the PDF (no new backend column needed; the
    // chart renderer ignores unknown keys on `data`).
    const resolved = metadata?.resolved_time_range || null;
    // PRD-1467: the stored chart array is display-capped (<=100), so the drill-down
    // scope message counted 100 against a larger result. Nest the true total_points
    // (a sibling of `data` in the query response, dropped until now) so the backend's
    // freeze can state the real count. Same pattern as resolved_time_range; the chart
    // renderer ignores unknown keys. total_rows for tables already rides on `data`.
    const overlay = {};
    if (resolved) overlay.resolved_time_range = resolved;
    if (typeof metadata?.total_points === 'number') overlay.total_points = metadata.total_points;
    const persistData = Object.keys(overlay).length ? { ...data, ...overlay } : data;
    setWidgets((prev) => prev.map((w) => (w.id === id ? { ...w, data: persistData, last_computed_at: nowIso } : w)));
    const w = widgets.find((x) => x.id === id);
    if (!w) return;
    // Don't persist for a widget you only see via org-sharing: the write is owner-only
    // server-side, so a viewer's refresh would fire a guaranteed 404 every time (Cody #4).
    if (w.is_shared) return;
    // Narrow write-back: PATCH only the fresh result + compute time, so a background
    // refresh can't overwrite a concurrent config edit or reset canvas size (Cody #4).
    // Strips on the way out only: the in-session copy above keeps whatever came back,
    // so nothing on screen changes. This write-back fires on EVERY successful refresh,
    // which makes it the noisier of the two blocked paths.
    apiService.patchWidgetData(id, withoutServerOnlyFields(persistData))
      // The server's row, but keeping the compute time already set above. One refresh
      // must move `last_computed_at` ONCE: the optimistic write and this echo carry
      // two different strings for the same event, and WidgetCard treats each move as
      // a possible edit. It can only recognise one of them as its own, so the second
      // cleared its live metadata — and with it the truncation overlay, one render
      // after a capped chart earned it.
      //
      // The two values describe the same moment to within the round trip, and this
      // one is already on screen, so preferring it also avoids a visible jump.
      .then((updated) =>
        setWidgets((cur) =>
          cur.map((x) => (x.id === updated.id ? { ...updated, last_computed_at: nowIso } : x)),
        ),
      )
      .catch(() => { /* viewer without write access, or transient — keep the optimistic copy */ });
  }, [widgets]);

  const handleRemove = useCallback(async (id) => {
    const ok = await confirm({
      title: 'Delete widget',
      message: 'Delete this widget from your dashboard? This action cannot be undone.',
      confirmLabel: 'Delete',
      variant: 'danger',
    });
    if (!ok) return;
    apiService.deleteWidget(id)
      .then(() => setWidgets((prev) => prev.filter((w) => w.id !== id)))
      .catch((err) => {
        console.error('[Insights] Failed to delete widget:', err);
        onShowNotification?.('Failed to delete widget', 'error');
      });
  }, [confirm, onShowNotification]);

  const handleEdit = useCallback((widget) => {
    setEditingWidget(widget);
    setShowCreator(true);
  }, []);

  const handleEditText = useCallback((widget) => {
    if (!textEditEnabled) return;
    setTextEditingWidget(widget);
  }, [textEditEnabled]);

  // Resolves true on success so the dialog can close, false to keep the typed text.
  // Merges only the words from the response (see withTextEdit): taking the whole row
  // would move `last_computed_at` and reset a card that refreshed this session.
  const handleSaveText = useCallback((widget, fields) => (!textEditEnabled ? Promise.resolve(false) :
    apiService.patchWidgetText(widget.id, fields)
      .then((updated) => {
        setWidgets((prev) => prev.map((w) => (w.id === updated.id ? withTextEdit(w, updated) : w)));
        return true;
      })
      .catch((err) => {
        console.error('[Insights] Failed to update widget text:', err);
        onShowNotification?.('Failed to update widget', 'error');
        return false;
      })
  ), [onShowNotification, textEditEnabled]);

  // PRD-1370 #8/#9: Share is a first-class action. The update endpoint sets the
  // scalar fields unconditionally, so a visibility-only PUT would wipe them — send
  // the full widget with visibility flipped (mirrors the Edit save payload).
  const shareOne = useCallback((widget, nextVisibility) => {
    const payload = {
      title: widget.title,
      widget_type_id: widget.widget_type_id,
      prompt: widget.prompt,
      dataset_ids: widget.dataset_ids,
      x_label: widget.x_label,
      y_label: widget.y_label,
      width: widget.width,
      widget_size: widget.widget_size,
      time_range: widget.time_range,
      refresh_policy: widget.refresh_policy,
      refresh_time: widget.refresh_time,
      refresh_interval_minutes: widget.refresh_interval_minutes,
      visibility: nextVisibility,
    };
    return apiService.updateWidget(widget.id, payload)
      .then((updated) => setWidgets((prev) => prev.map((w) => (w.id === updated.id ? updated : w))));
  }, []);

  const handleShare = useCallback((widget) => {
    const next = widget.visibility === 'organization' ? 'private' : 'organization';
    shareOne(widget, next)
      .then(() => onShowNotification?.(
        next === 'organization' ? 'Widget shared with your organization' : 'Widget set to private',
        'success',
      ))
      .catch((err) => {
        console.error('[Insights] Failed to update sharing:', err);
        onShowNotification?.('Failed to update sharing', 'error');
      });
  }, [shareOne, onShowNotification]);

  const handleShareDashboard = useCallback(async () => {
    setDashMenuOpen(false);
    // Gate the handler too, not just its button (Console rule): a stale menu or a
    // direct call must not share when the flag is off.
    if (!sharingEnabled) return;
    // With tabs, sharing the dashboard means sharing its tabs, so viewers get the
    // layout and not a loose pile of widgets.
    if (tabsEnabled) {
      await tabsApi.shareAll();
      return;
    }
    const mine = widgets.filter((w) => w.visibility !== 'organization');
    if (mine.length === 0) {
      onShowNotification?.('All your widgets are already shared with your organization', 'info');
      return;
    }
    const ok = await confirm({
      title: 'Share dashboard',
      message: `Share all ${mine.length} of your widget(s) with your organization? Anyone in your org will be able to view them.`,
      confirmLabel: 'Share',
    });
    if (!ok) return;
    // allSettled, not all: Promise.all is fail-fast, so a single rejection would
    // report "Failed to share dashboard" even though some widgets did get shared.
    // Count outcomes and tell the user exactly what happened (review on #188).
    const results = await Promise.allSettled(mine.map((w) => shareOne(w, 'organization')));
    const failed = results.filter((r) => r.status === 'rejected');
    if (failed.length === 0) {
      onShowNotification?.('Dashboard shared with your organization', 'success');
    } else if (failed.length === mine.length) {
      console.error('[Insights] Failed to share dashboard:', failed[0].reason);
      onShowNotification?.('Failed to share dashboard', 'error');
    } else {
      onShowNotification?.(
        `Shared ${mine.length - failed.length} of ${mine.length} widget(s); ${failed.length} failed`,
        'error',
      );
    }
  }, [sharingEnabled, tabsEnabled, tabsApi, widgets, confirm, shareOne, onShowNotification]);

  const handleCloseModal = useCallback(() => {
    setShowCreator(false);
    setEditingWidget(null);
  }, []);

  // Set when the user finishes a resize or drag; the grid's next layout change is
  // theirs. The grid also reports a "change" when a tab first renders, and saving
  // other people's widgets then would freeze a copy of the owner's layout for every
  // viewer who merely looked -- after which the owner's rearrangements never reach them.
  // Holds the id of the tab the user arranged on, not just "arranged", so it can never
  // carry to another tab: the grid reports a new tab's layout during its own update,
  // before any effect of ours could reset a plain flag.
  const userArrangedRef = useRef(null);
  // Only a stop that actually moved or resized something counts. The grid calls
  // onDragStop on every mouseup over a card header -- a plain click included -- but
  // calls onLayoutChange only when the layout changed, so a click would otherwise
  // leave the flag set for whatever layout change came next.
  const activeTabId = activeTab?.id;
  const markUserArranged = useCallback((_layout, oldItem, newItem) => {
    if (!oldItem || !newItem) return;
    if (['x', 'y', 'w', 'h'].some((k) => oldItem[k] !== newItem[k])) userArrangedRef.current = activeTabId;
  }, [activeTabId]);

  const handleLayoutChange = useCallback((_currentLayout, allLayouts) => {
    if (!tabsEnabled) {
      setSavedLayouts(allLayouts);
      // Fallen back from tabs for this visit: arrange freely, store nothing. Writing
      // the old browser layout now would put back a copy the server has replaced.
      if (!tabsUnavailable) saveLayouts(allLayouts);
      return;
    }
    if (!activeTab) return;
    const userArranged = userArrangedRef.current === activeTab.id;
    userArrangedRef.current = null;
    // Your own widgets are saved on any change, as the single-page dashboard always
    // did. Other people's only when you resized or moved something yourself.
    const ids = userArranged ? savableIds : savableIds.filter((id) => !widgets.find((w) => w.id === id)?.is_shared);
    const payload = layoutPayload(allLayouts, ids);
    const before = Object.fromEntries(ids.filter((id) => tabLayouts[id]).map((id) => [id, tabLayouts[id]]));
    if (Object.keys(payload).length === 0 || sameLayout(payload, before)) return;
    // The server stores one request two ways -- your own widgets on the widget, anyone
    // else's in your own copy -- and the screen is updated the same way.
    const isOthers = (id) => widgets.find((w) => w.id === id)?.is_shared;
    const othersAfter = Object.fromEntries(Object.entries(payload).filter(([id]) => isOthers(id)));
    const othersBefore = activeTab.layout_overrides || null;
    setWidgets((prev) => prev.map((w) => (!w.is_shared && payload[w.id] ? { ...w, layout: payload[w.id] } : w)));
    if (userArranged) {
      tabsApi.setLayoutOverrides(activeTab.id, Object.keys(othersAfter).length ? othersAfter : null);
    }
    apiService.saveDashboardTabLayout(activeTab.id, payload).catch((err) => {
      console.error('[Insights] Failed to save layout:', err);
      setWidgets((prev) => prev.map((w) => (
        !w.is_shared && w.id in payload ? { ...w, layout: before[w.id] || null } : w
      )));
      tabsApi.setLayoutOverrides(activeTab.id, othersBefore);
      onShowNotification?.('Could not save the layout. Your last saved arrangement is shown.', 'error');
    });
  }, [tabsEnabled, tabsUnavailable, activeTab, savableIds, tabLayouts, widgets, tabsApi, onShowNotification]);

  const handleMoveToTab = useCallback(async (widget, tabId) => {
    if (!tabsEnabled) return;
    try {
      await apiService.moveWidgetToTab(widget.id, tabId);
      // No position: it is appended below everything already on the target tab.
      setWidgets((prev) => prev.map((w) => (w.id === widget.id ? { ...w, tab_id: tabId, layout: null } : w)));
      const target = tabsApi.tabs.find((t) => t.id === tabId);
      onShowNotification?.(`Moved to "${target?.name || 'tab'}"`, 'success');
    } catch (err) {
      console.error('[Insights] Failed to move widget:', err);
      onShowNotification?.(err?.message || 'Could not move the widget', 'error');
    }
  }, [tabsEnabled, tabsApi.tabs, onShowNotification]);

  const widgetCountOn = useCallback(
    (tabId) => widgets.filter((w) => w.tab_id === tabId).length,
    [widgets],
  );

  const handleDeleteTab = useCallback(async (tab) => {
    const deleted = await tabsApi.deleteTab(tab, widgetCountOn(tab.id));
    if (!deleted) return;
    // The server moved the widgets; read them back rather than guess where they went.
    reloadWidgets().catch((err) => {
      console.error('[Insights] Failed to reload widgets:', err);
      onShowNotification?.('Failed to load widgets', 'error');
    });
  }, [tabsApi, widgetCountOn, reloadWidgets, onShowNotification]);

  // PRD-1337: open (or reuse) the widget's drill-down conversation, then deep-link
  // into the EXISTING Ask ResAI page on that thread (not a second chat surface — the
  // PRD non-goal). Ask ResAI shows the widget's chart alongside the conversation. A
  // 403 means the viewer can't read the widget's dataset — the backend refuses to
  // open a conversation (sharing is not a grant).
  const handleDrilldown = useCallback(async (widget, { reasoning = false } = {}) => {
    if (!drilldownEnabled) return;
    try {
      const session = await apiService.openWidgetConversation(widget.id);
      // PRD-1467: the thread id must be a real UUID before it goes in the URL. A
      // missing/renamed id would navigate to `?drilldown_thread=undefined`, which the
      // Ask ResAI open effect rejects (its UUID guard) — the user lands on a blank
      // composer instead of the bound thread. Treat a bad id as an open failure.
      const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      if (!session?.thread_id || !UUID_RE.test(session.thread_id)) {
        throw new Error('Drill-down response had no valid thread id');
      }
      // Carry the reused thread id in a dedicated URL param (distinct from the report
      // ?thread= deep-link, which is stripped after opening). Keeping it in the URL
      // means the conversation + chart pane survive a browser refresh, and re-opening
      // always lands on the SAME reused thread with its appended turns.
      // "Show reasoning" lands with the widget's reasoning open; "Ask a follow-up" lands
      // on the conversation with it closed.
      navigate(`/?drilldown_thread=${session.thread_id}${reasoning ? '&reasoning=open' : ''}`);
    } catch (err) {
      console.error('[Insights] Drill-down open failed:', err);
      const msg = err?.status === 403
        ? "You don't have access to this widget's dataset, so it can't be drilled into."
        : 'Could not open a conversation for this widget';
      onShowNotification?.(msg, 'error');
    }
  }, [drilldownEnabled, onShowNotification, navigate]);

  // PRD-1336 R6: export the dashboard to PDF via the browser's print-to-PDF. We
  // authorize server-side FIRST — that call 403s if the user can't read a dataset
  // behind any widget on the page (a shared dashboard must not leak an unreadable
  // dataset) and writes the audit entry — and only open the print dialog once it
  // succeeds, so a refused export never produces a file. The @media print rules in
  // Insights.css strip the app chrome and lay the widgets out for paper.
  // With tabs, the PDF is the tab on screen, and it is that tab's widgets that are
  // authorized -- the print renders only what the grid is showing.
  const handleExport = useCallback(async () => {
    if (!exportEnabled || exporting || shownWidgets.length === 0) return;
    setExporting(true);
    try {
      await apiService.authorizeDashboardExport(shownWidgets.map((w) => w.id));
      // Let the print-only header render before the (blocking) print dialog opens.
      await new Promise((resolve) => setTimeout(resolve, 50));
      window.print();
    } catch (err) {
      console.error('[Insights] Export failed:', err);
      const msg = err?.status === 403
        ? "You don't have access to a dataset behind one of these widgets, so this dashboard can't be exported."
        : 'Failed to export dashboard';
      onShowNotification?.(msg, 'error');
    } finally {
      setExporting(false);
    }
  }, [exportEnabled, exporting, shownWidgets, onShowNotification]);

  const greetingText = displayName ? `${greeting}, ${displayName}` : greeting;
  const layouts = tabsEnabled
    ? buildLayouts(shownWidgets, savedLayoutsFromWidgets(shownWidgets.map((w) => ({ ...w, layout: tabLayouts[w.id] || null }))))
    : buildLayouts(widgets, savedLayouts);
  const hiddenTabs = tabsEnabled ? tabsApi.tabs.filter((t) => t.hidden) : [];
  const moveTargetsFor = (w) => (
    tabsEnabled && !w.is_shared ? ownTabs(tabsApi.tabs).filter((t) => t.id !== w.tab_id) : undefined
  );
  const showDashMenu = tabsEnabled ? (shownWidgets.length > 0 || hiddenTabs.length > 0) : widgets.length > 0;

  // Close the dashboard actions menu on an outside click.
  useEffect(() => {
    if (!dashMenuOpen) return undefined;
    const onDocClick = (e) => {
      if (dashMenuRef.current && !dashMenuRef.current.contains(e.target)) setDashMenuOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [dashMenuOpen]);

  return (
    <div className="insights">
      <div className="insights-header">
        <div className="insights-greeting-section">
          <h1 className="insights-greeting">{greetingText}</h1>
          <p className="insights-subtitle">Here's your latest insights</p>
        </div>
        <div className="insights-header-actions">
          <button
            className="insights-add-widget-btn"
            onClick={() => setShowCreator(true)}
            disabled={viewingShared}
            title={viewingShared ? 'Widgets can only be added to your own tabs' : undefined}
          >
            <Icon name="add" className="insights-add-icon" size={18} />
            Add widget
          </button>
          {/* PRD-1370 #5: dashboard actions moved into a 3-dot menu next to Add widget. */}
          {showDashMenu && (
            <div className="insights-dash-menu-wrap" ref={dashMenuRef}>
              <button
                className="insights-dash-menu-btn"
                onClick={() => setDashMenuOpen((o) => !o)}
                title="Dashboard actions"
                aria-label="Dashboard actions"
              >
                <Icon name="more_vert" />
              </button>
              {dashMenuOpen && (
                <div className="insights-dash-dropdown">
                  {/* Nothing to print on an empty tab, and the export would refuse. */}
                  {exportEnabled && shownWidgets.length > 0 && (
                    <button
                      onClick={() => { setDashMenuOpen(false); handleExport(); }}
                      disabled={exporting}
                    >
                      {exporting ? 'Preparing…' : 'Export PDF'}
                    </button>
                  )}
                  {sharingEnabled && (!tabsEnabled || tabsApi.canShare) && (
                    <button onClick={handleShareDashboard}>Share dashboard</button>
                  )}
                  {/* Tabs shared with you that you hid, to bring back. */}
                  {hiddenTabs.map((t) => (
                    <button
                      key={t.id}
                      onClick={() => { setDashMenuOpen(false); tabsApi.setHidden(t, false); }}
                    >
                      {`Show "${t.name}"`}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* PRD-1336 R6: print-only cover header — hidden on screen, shown on the PDF. */}
      <div className="insights-print-header" aria-hidden="true">
        <span className="insights-print-title">
          {activeTab ? `Insights dashboard: ${activeTab.name}` : 'Insights dashboard'}
        </span>
        <span className="insights-print-date">{formatTimestamp(printedAt, 'static')}</span>
      </div>

      {tabsEnabled && tabsApi.loaded && (
        <DashboardTabBar
          tabs={visibleTabs(tabsApi.tabs)}
          activeTabId={activeTab?.id}
          canShare={tabsApi.canShare}
          onSelect={tabsApi.selectTab}
          onCreate={tabsApi.createTab}
          onRename={tabsApi.renameTab}
          onShare={(tab, share) => tabsApi.setSharing(tab, share, widgetCountOn(tab.id))}
          onMove={tabsApi.moveTab}
          onDelete={handleDeleteTab}
          onHide={(tab) => tabsApi.setHidden(tab, true)}
        />
      )}

      {shownWidgets.length === 0 ? (
        <div className="insights-widget-area">
          {viewingShared ? (
            <div className="insights-empty-state">
              <p className="insights-empty-title">Nothing on this tab yet.</p>
              <p className="insights-empty-subtitle">Widgets its owner adds will appear here.</p>
            </div>
          ) : (
            <div className="insights-empty-state">
              <p className="insights-empty-title">{tabsEnabled ? 'No widgets on this tab yet.' : 'No widgets yet.'}</p>
              <p className="insights-empty-subtitle">Add a widget to start building your dashboard.</p>
              <button className="insights-empty-add-btn" onClick={() => setShowCreator(true)}>Add widget</button>
            </div>
          )}
        </div>
      ) : (
        <ResponsiveGridLayout
          className="insights-grid"
          layouts={layouts}
          breakpoints={BREAKPOINTS}
          cols={COLS}
          rowHeight={ROW_HEIGHT}
          margin={[16, 16]}
          containerPadding={[0, 10]}
          draggableHandle=".wc-header"
          onLayoutChange={handleLayoutChange}
          onResizeStop={markUserArranged}
          onDragStop={markUserArranged}
          compactType="vertical"
          // On a tab shared with you, you can move and resize cards for your own
          // viewing. That is saved for you alone and never changes what the owner or
          // anyone else sees; the tab's contents stay the owner's to change.
          isDraggable
          isResizable
        >
          {shownWidgets.map((w) => (
            <div key={w.id} className="insights-grid-item">
              <WidgetCard
                widget={w}
                onRemove={handleRemove}
                onEdit={handleEdit}
                onEditText={textEditEnabled ? handleEditText : undefined}
                onShare={handleShare}
                onRefreshed={handleWidgetRefreshed}
                onDrilldown={drilldownEnabled ? handleDrilldown : undefined}
                onShowNotification={notifyWidgetFailure}
                moveTargets={moveTargetsFor(w)}
                onMoveToTab={tabsEnabled ? handleMoveToTab : undefined}
              />
            </div>
          ))}
        </ResponsiveGridLayout>
      )}

      <WidgetCreatorModal
        open={showCreator}
        onClose={handleCloseModal}
        onSave={handleSave}
        editingWidget={editingWidget}
      />
      <WidgetTextEditModal
        key={textEditingWidget?.id || 'none'}
        widget={textEditingWidget}
        onClose={() => setTextEditingWidget(null)}
        onSave={handleSaveText}
      />
      {confirmDialog}
    </div>
  );
}

export default Insights;
